import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { Header } from 'components/common/header/Header';
import { useMeetingDetail, useMeetingMembers } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import './RealtimeLocation.css';

interface ParticipantLocation {
    id: string;
    name: string;
    status: 'arriving' | 'arrived' | 'not-started' | 'not-attending' | 'undecided' | 'no-sharing';
    distance: string;
    eta: string;
    lastUpdated: string;
    attendanceStatus: string;
    isLocationSharing: boolean;
    showOnMap: boolean;
    latitude?: number;
    longitude?: number;
}

// 더미 위치 데이터 (실제로는 서버에서 받아옴)
const DUMMY_LOCATIONS: Record<string, { lat: number; lng: number }> = {
    'user-001': { lat: 37.5665, lng: 126.9780 },  // 서울시청 근처
    'user-002': { lat: 37.5700, lng: 126.9820 },  // 광화문 근처
    'user-003': { lat: 37.5635, lng: 126.9750 },  // 남대문 근처
    'mock-user-001': { lat: 37.5680, lng: 126.9800 },
};

export const RealtimeLocation = () => {
    const { meetingId } = useParams();
    const { data: meetingData } = useMeetingDetail();
    const { data: membersData } = useMeetingMembers();

    // 현재 사용자 ID
    const currentUserId = sessionStorage.getItem('userId') || 'mock-user-001';

    // 내 위치 공유 설정 (localStorage에서 불러오기)
    const [shareLocation, setShareLocation] = useState(() => {
        const saved = localStorage.getItem(`locationSharing_${meetingId}_${currentUserId}`);
        return saved !== 'false'; // 기본값은 true (공유)
    });

    // 컴포넌트 마운트 시 모임 정보 및 멤버 정보 조회
    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingDetail(meetingId);
            meetingStore.onGetMeetingMembers(meetingId);
        }
    }, [meetingId]);

    // API 데이터에서 모임 장소 정보 추출
    const meetingLocation = {
        name: meetingData?.data?.placeName || '장소 미정',
        address: meetingData?.data?.placeAddress || '',
        latitude: meetingData?.data?.latitude,
        longitude: meetingData?.data?.longitude
    };

    // 지도 URL 생성
    const getMapUrl = () => {
        if (meetingLocation.latitude && meetingLocation.longitude) {
            return `https://maps.google.com/maps?q=${meetingLocation.latitude},${meetingLocation.longitude}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
        } else if (meetingLocation.address) {
            return `https://maps.google.com/maps?q=${encodeURIComponent(meetingLocation.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
        } else {
            return `https://maps.google.com/maps?q=${encodeURIComponent(meetingLocation.name)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
        }
    };

    // 두 좌표 간 거리 계산 (미터 단위)
    const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
        const R = 6371000; // 지구 반경 (미터)
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    // 거리를 보기 좋은 형식으로 변환
    const formatDistance = (meters: number): string => {
        if (meters < 100) return '도착';
        if (meters < 1000) return `${Math.round(meters)}m`;
        return `${(meters / 1000).toFixed(1)}km`;
    };

    // API 데이터에서 참여자 목록 생성
    const participants: ParticipantLocation[] = (membersData?.data || []).map((member: any, index: number) => {
        const attendanceStatus = member.attendanceStatus || 'UNDECIDED';
        // 위치 공유 여부: localStorage에 명시적으로 'false'로 설정된 경우만 비공개
        // 설정이 없거나 'true'면 공개로 간주 (참석 확정자의 경우)
        const locationSharingSetting = localStorage.getItem(`locationSharing_${meetingId}_${member.userId}`);
        const isLocationSharing = locationSharingSetting !== 'false';

        // 더미 위치 데이터 할당 (실제로는 서버에서 받아옴)
        // 사용자 ID로 더미 데이터가 없으면 모임 장소 근처에 랜덤 위치 생성
        let dummyLocation = DUMMY_LOCATIONS[member.userId];
        if (!dummyLocation && meetingLocation.latitude && meetingLocation.longitude) {
            // 모임 장소 근처 랜덤 위치 (약 500m ~ 2km 반경)
            const offset = (index + 1) * 0.003;
            dummyLocation = {
                lat: parseFloat(meetingLocation.latitude) + (Math.random() - 0.5) * offset,
                lng: parseFloat(meetingLocation.longitude) + (Math.random() - 0.5) * offset
            };
        }

        // 참석 상태와 위치 공유 여부에 따른 상태 결정
        let status: ParticipantLocation['status'];
        let showOnMap = false;
        let distance = '-';
        let eta = '-';
        let lastUpdated = '-';
        let latitude: number | undefined;
        let longitude: number | undefined;

        if (attendanceStatus === 'NOT_ATTENDING') {
            status = 'not-attending';
        } else if (attendanceStatus === 'UNDECIDED') {
            status = 'undecided';
        } else if (!isLocationSharing) {
            // 참석 확정이지만 위치 공유를 끈 경우
            status = 'no-sharing';
        } else {
            // 참석 확정 + 위치 공유 중인 경우
            showOnMap = true;
            latitude = dummyLocation?.lat;
            longitude = dummyLocation?.lng;

            // 거리 계산
            if (latitude && longitude && meetingLocation.latitude && meetingLocation.longitude) {
                const distanceMeters = calculateDistance(
                    latitude, longitude,
                    parseFloat(meetingLocation.latitude), parseFloat(meetingLocation.longitude)
                );
                distance = formatDistance(distanceMeters);

                // 상태 결정 (100m 이내면 도착)
                if (distanceMeters < 100) {
                    status = 'arrived';
                } else {
                    status = 'arriving';
                }

                // 예상 도착 시간 (도보 기준 약 5km/h = 약 83m/분)
                const etaMinutes = Math.round(distanceMeters / 83);
                eta = etaMinutes <= 1 ? '곧 도착' : `약 ${etaMinutes}분`;
            } else {
                status = 'arriving';
            }
            lastUpdated = '방금 전';
        }

        return {
            id: member.userId,
            name: member.nickname || member.userId || '사용자',
            status,
            distance,
            eta,
            lastUpdated,
            attendanceStatus,
            isLocationSharing,
            showOnMap,
            latitude,
            longitude
        };
    });

    // 지도에 표시할 참여자 (참석 확정 + 위치 공유 중)
    const participantsOnMap = participants.filter(p => p.showOnMap);

    // 참여자 목록 정렬: 위치 공유 중 > 위치 비공개 > 미정 > 불참
    const sortedParticipants = [...participants].sort((a, b) => {
        const statusOrder: Record<string, number> = {
            'arrived': 0,
            'arriving': 1,
            'not-started': 2,
            'no-sharing': 3,
            'undecided': 4,
            'not-attending': 5
        };
        return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
    });

    // 도착한 참여자 수
    const arrivedCount = participants.filter(p => p.status === 'arrived').length;

    // 참여자 마커 위치 계산 (지도 영역 내 상대 위치)
    const getMarkerPosition = useMemo(() => {
        if (!meetingLocation.latitude || !meetingLocation.longitude) {
            return () => ({ top: '50%', left: '50%' });
        }

        const centerLat = parseFloat(meetingLocation.latitude);
        const centerLng = parseFloat(meetingLocation.longitude);

        // 지도 영역 (약 2km x 2km)
        const latRange = 0.02;
        const lngRange = 0.025;

        return (lat: number, lng: number) => {
            const latOffset = (lat - centerLat) / latRange;
            const lngOffset = (lng - centerLng) / lngRange;

            // 중앙 기준 위치 계산 (%)
            const top = 50 - latOffset * 100;
            const left = 50 + lngOffset * 100;

            // 범위 제한 (10% ~ 90%)
            return {
                top: `${Math.max(10, Math.min(90, top))}%`,
                left: `${Math.max(10, Math.min(90, left))}%`
            };
        };
    }, [meetingLocation]);

    // 마커 색상 배열
    const markerColors = ['#4285F4', '#34A853', '#9C27B0', '#FF9800', '#E91E63', '#00BCD4'];

    const handleRefresh = () => {
        // Refresh location data
        console.log('위치 정보 갱신');
        if (meetingId) {
            meetingStore.onGetMeetingMembers(meetingId);
        }
    };

    const getStatusInfo = (participant: ParticipantLocation) => {
        switch (participant.status) {
            case 'arrived':
                return { text: '도착', icon: '✓', className: 'arrived', description: '' };
            case 'arriving':
                return { text: '이동 중', icon: '🚶', className: 'arriving', description: '' };
            case 'not-started':
                return { text: '이동 전', icon: '⏸', className: 'not-started', description: '' };
            case 'not-attending':
                return { text: '불참', icon: '❌', className: 'not-attending', description: '모임에 불참합니다' };
            case 'undecided':
                return { text: '미정', icon: '❓', className: 'undecided', description: '참석 여부 미정' };
            case 'no-sharing':
                return { text: '위치 비공개', icon: '🔒', className: 'no-sharing', description: '위치 공유를 하지 않습니다' };
            default:
                return { text: '알 수 없음', icon: '?', className: 'unknown', description: '' };
        }
    };

    return (
        <div className="realtime-location-wrapper">
            <Header title="실시간 위치" isSubPage />

            <div className="realtime-location-content">
                {/* Map Container */}
                <div className="location-map-container">
                    <div className="location-map-wrapper">
                        {/* Google Maps iframe */}
                        <iframe
                            className="location-map-iframe"
                            src={getMapUrl()}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Meeting Location Map"
                        />

                        {/* 참여자 마커 오버레이 */}
                        <div className="participants-markers-overlay">
                            {/* 목적지 마커 */}
                            <div className="map-marker destination-marker" style={{ top: '50%', left: '50%' }}>
                                <div className="marker-pin destination">📍</div>
                                <div className="marker-tooltip">{meetingLocation.name}</div>
                            </div>

                            {/* 참여자 마커들 */}
                            {participantsOnMap.map((participant, index) => {
                                if (!participant.latitude || !participant.longitude) return null;
                                const position = getMarkerPosition(participant.latitude, participant.longitude);
                                const color = markerColors[index % markerColors.length];

                                return (
                                    <div
                                        key={participant.id}
                                        className={`map-marker participant-marker ${participant.status === 'arrived' ? 'arrived' : ''}`}
                                        style={{ top: position.top, left: position.left }}
                                    >
                                        <div
                                            className="marker-pin participant"
                                            style={{ backgroundColor: color }}
                                        >
                                            {participant.name[0]}
                                        </div>
                                        <div className="marker-tooltip">
                                            <div className="tooltip-name">{participant.name}</div>
                                            <div className="tooltip-status">
                                                {participant.status === 'arrived' ? '✓ 도착' : `🚶 ${participant.distance}`}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* 범례 */}
                        <div className="map-legend">
                            <div className="legend-item">
                                <span className="legend-marker destination">📍</span>
                                <span className="legend-label">목적지</span>
                            </div>
                            {participantsOnMap.map((p, i) => (
                                <div key={p.id} className="legend-item">
                                    <span
                                        className="legend-marker participant"
                                        style={{ color: markerColors[i % markerColors.length] }}
                                    >●</span>
                                    <span className="legend-label">{p.name}</span>
                                </div>
                            ))}
                        </div>

                        {/* Refresh Button */}
                        <button className="refresh-button" onClick={handleRefresh}>
                            <span className="refresh-icon">🔄</span>
                        </button>
                    </div>
                </div>

                {/* Location Settings */}
                <div className="location-settings">
                    <div className="setting-item">
                        <div className="setting-info">
                            <span className="setting-label">내 위치 공유</span>
                            <span className="setting-description">
                                {shareLocation ? '다른 참여자들에게 내 위치가 공유됩니다' : '내 위치가 공유되지 않습니다'}
                            </span>
                        </div>
                        <button
                            className={`toggle-switch ${shareLocation ? 'active' : ''}`}
                            onClick={() => {
                                const newValue = !shareLocation;
                                setShareLocation(newValue);
                                // localStorage에 저장
                                localStorage.setItem(`locationSharing_${meetingId}_${currentUserId}`, String(newValue));
                            }}
                        >
                            <div className="toggle-knob"></div>
                        </button>
                    </div>
                </div>

                {/* Participants List */}
                <div className="location-participants">
                    <div className="participants-header">
                        <div className="participants-header-left">
                            <h3 className="participants-title">참여자 위치</h3>
                            <span className="participants-count">
                                {participantsOnMap.length}/{participants.length}명 위치 공유 중
                            </span>
                        </div>
                        <button className="refresh-button-small" onClick={handleRefresh}>
                            🔄
                        </button>
                    </div>

                    <div className="participants-location-list">
                        {sortedParticipants.map(participant => {
                            const statusInfo = getStatusInfo(participant);
                            const isInactive = !participant.showOnMap;

                            return (
                                <div key={participant.id} className={`participant-location-item ${isInactive ? 'inactive' : ''}`}>
                                    <div className={`participant-location-avatar ${isInactive ? 'inactive' : ''}`}>
                                        {participant.name[0]}
                                    </div>
                                    <div className="participant-location-info">
                                        <div className="participant-location-name">
                                            {participant.name}
                                        </div>
                                        <div className="participant-location-details">
                                            <span className={`location-status ${statusInfo.className}`}>
                                                {statusInfo.icon} {statusInfo.text}
                                            </span>
                                            {participant.showOnMap && (
                                                <>
                                                    <span className="location-separator">•</span>
                                                    <span className="location-distance">{participant.distance}</span>
                                                </>
                                            )}
                                        </div>
                                        {statusInfo.description && (
                                            <div className="participant-location-description">
                                                {statusInfo.description}
                                            </div>
                                        )}
                                    </div>
                                    {participant.showOnMap && (
                                        <div className="participant-location-eta">
                                            <div className="eta-text">{participant.eta}</div>
                                            <div className="last-updated">{participant.lastUpdated}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
